import { resolveCanonicalProduct } from '../utils/reviewedProductIdentity';
import React, { useState, useEffect, memo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ModalProductCard from './ModalProductCard';
import { getCanonicalProductSync } from '../utils/syncProductMatcher';
import { getCanonicalProductInfo } from '../utils/productMatcher';
import { trackGetDirections, trackProductView } from '../utils/analytics';
import { getStoreDirectionsUrl } from '../utils/storeDirections';
import './ExpandedStoreSection.scss';

/**
 * ExpandedStoreSection - A collapsible component that displays store details and products
 * This replaces the previous modal implementation with a more mobile-friendly expandable section
 */
const ExpandedStoreSection = memo(function ExpandedStoreSection({ 
  products, 
  location, 
  loading = false
}) {
  const [canonicalProductCache, setCanonicalProductCache] = useState(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(6); // Default value, will be updated
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const productListRef = useRef(null);
  const sectionRef = useRef(null); // Reference to the entire expanded section
  
  // Swipe functionality state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [hasUserSwiped, setHasUserSwiped] = useState(false);
  const swipeAreaRef = useRef(null);
  const minSwipeDistance = 50; // Minimum distance for a swipe to be detected
  const directionsUrl = getStoreDirectionsUrl(location);
  
  // Calculate how many products can fit in a row and update productsPerPage
  const calculateProductsPerRow = () => {
    if (!productListRef.current) return;
    
    // Get the width of the product list container
    const containerWidth = productListRef.current.offsetWidth;
    
    // Check if we're in mobile view
    const isMobileView = window.innerWidth <= 768;
    
    if (isMobileView) {
      // For mobile, we always want 2 cards per row and 2 rows (4 cards total per page)
      setProductsPerPage(4);
      return;
    }
    
    // For desktop/tablet, continue with the adaptive calculation
    // Using the known fixed width of each card (180px) plus gap (16px)
    const cardWidth = 190; // Fixed card width (matches minmax in CSS)
    const gapWidth = 16; // Gap between cards (matches gap in CSS)
    const containerPadding = 24; // Account for container padding
    
    // Available width for cards
    const availableWidth = containerWidth - containerPadding;
    
    // Calculate how many cards can fit in a row with proper spacing
    // We subtract one gapWidth from availableWidth because we need (n-1) gaps for n cards
    const cardsPerRow = Math.max(1, Math.floor((availableWidth + gapWidth) / (cardWidth + gapWidth)));
    
    // Apply a safety factor to prevent squashing
    // If cards would be compressed more than 5%, reduce the count by 1
    const totalWidthNeeded = (cardsPerRow * cardWidth) + ((cardsPerRow - 1) * gapWidth);
    const isTooTight = totalWidthNeeded > availableWidth;
    
    // Final card count with safety adjustment
    const finalCardsPerRow = isTooTight ? Math.max(1, cardsPerRow - 1) : cardsPerRow;
    
    // If the calculated value is different from the current state, update it
    if (finalCardsPerRow !== productsPerPage) {
      setProductsPerPage(finalCardsPerRow);
      
      // If we're not on the first page, check if we need to adjust current page
      if (currentPage > 1) {
        const newTotalPages = Math.ceil(products.length / finalCardsPerRow);
        if (currentPage > newTotalPages) {
          setCurrentPage(Math.max(1, newTotalPages));
        }
      }
    }
  };
  
  // Handle window resize and any other layout changes that might affect the grid
  useEffect(() => {
    let resizeTimer = null;
    
    const handleResize = () => {
      // Debounce the resize calculations for better performance
      if (resizeTimer) clearTimeout(resizeTimer);
      
      resizeTimer = setTimeout(() => {
        const currentIsMobile = window.innerWidth <= 768;
        setIsMobile(currentIsMobile);
        
        // Reset to page 1 when switching between mobile/desktop to prevent empty pages
        if (currentIsMobile !== isMobile) {
          setCurrentPage(1);
          
          // Force update products per page immediately for a smoother transition
          if (currentIsMobile) {
            setProductsPerPage(4); // Force 4 products per page (2x2) on mobile
          }
        }
        
        // Recalculate products per row
        calculateProductsPerRow();
      }, 100);
    };
    
    // Initial calculation
    calculateProductsPerRow();
    
    // Set up resize listener
    window.addEventListener('resize', handleResize);
    
    // Create a ResizeObserver to detect container size changes
    if (typeof ResizeObserver !== 'undefined' && productListRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        calculateProductsPerRow();
      });
      
      resizeObserver.observe(productListRef.current);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', handleResize);
      };
    }
    
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, products.length]);
  

  // Pre-compute canonical products for all products
  useEffect(() => {
    // First try to get all products synchronously for immediate display
    const newCache = new Map();
    let needsAsyncLookup = false;
    
    // Try sync lookup first for all products
    products.forEach(product => {
      const canonical = getCanonicalProductSync(product.name);
      if (canonical) {
        newCache.set(product.name, canonical);
      } else {
        needsAsyncLookup = true;
      }
    });
    
    // Update with what we have immediately
    if (newCache.size > 0) {
      setCanonicalProductCache(newCache);
    }
    
    // If we need async lookup for any products, do it as a second pass
    if (needsAsyncLookup) {
      const preComputeCanonicalProducts = async () => {
        // Only process products that weren't found synchronously
        const missingProducts = products.filter(product => !newCache.has(product.name));
        
        const promises = missingProducts.map(async (product) => {
          try {
            const canonical = await getCanonicalProductInfo(product.name);
            return { key: product.name, value: canonical };
          } catch (error) {
            console.error('Error getting canonical product info:', error);
            return { key: product.name, value: null };
          }
        });
        
        const results = await Promise.all(promises);
        
        // Update the cache with async results
        const updatedCache = new Map(newCache);
        results.forEach(({ key, value }) => {
          if (value) updatedCache.set(key, value);
        });
        
        setCanonicalProductCache(updatedCache);
      };
      
      preComputeCanonicalProducts();
    }
  }, [products]);
  
  // Recalculate products per row when products are rendered
  useEffect(() => {
    // Allow a small delay for the products to render
    const timer = setTimeout(() => {
      calculateProductsPerRow();
    }, 50);
    
    // Set up a MutationObserver to detect when product cards are added to the DOM
    if (productListRef.current) {
      const observer = new MutationObserver(() => {
        calculateProductsPerRow();
      });
      
      observer.observe(productListRef.current, { 
        childList: true,
        subtree: true,
        attributeFilter: ['style', 'class']
      });
      
      return () => {
        observer.disconnect();
        clearTimeout(timer);
      };
    }
    
    return () => clearTimeout(timer);
  }, [products, canonicalProductCache, currentPage]);
  
  // Calculate pagination
  const totalPages = Math.ceil(products.length / productsPerPage);
  
  // For mobile view (4 cards per page), we're showing 2 rows of 2 cards
  // For other views, we show the number of cards calculated adaptively
  const currentProducts = products.slice(
    (currentPage - 1) * productsPerPage, 
    currentPage * productsPerPage
  );
  
  const handleGetDirections = (e) => {
    e.preventDefault();
    if (!directionsUrl) return;

    trackGetDirections(location);
    window.open(directionsUrl, '_blank', 'noopener,noreferrer');
  };

  // Handle swipe gestures for mobile (optional)
  const handleSwipe = (direction) => {
    if (direction === 'left' && currentPage < totalPages) {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    } else if (direction === 'right' && currentPage > 1) {
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }
  };
  
  // Touch event handlers for swipe functionality
  const onTouchStart = useCallback((e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  }, []);
  
  const onTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > minSwipeDistance;
    
    if (isSwipe) {
      if (distance > 0) {
        // Swiped left, go to next
        if (currentPage < totalPages) {
          setCurrentPage(prev => Math.min(prev + 1, totalPages));
          setHasUserSwiped(true);
        }
      } else {
        // Swiped right, go to previous
        if (currentPage > 1) {
          setCurrentPage(prev => Math.max(prev - 1, 1));
          setHasUserSwiped(true);
        }
      }
    }
    
    // Reset touch coordinates
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, minSwipeDistance, currentPage, totalPages]);
  
  // Gracefully handle transitions between different expanded store sections
  useEffect(() => {
    // Function to smoothly scroll to the store
    const scrollToStore = () => {
      if (!sectionRef.current) return;
      
      // Find the parent store row element that contains this expanded section
      const parentStoreRow = sectionRef.current.closest('.store-row');
      
      if (parentStoreRow) {
        // Get the position of the parent store row
        const storePosition = parentStoreRow.getBoundingClientRect().top + window.pageYOffset;
        
        // Position the store higher on the screen - just 5% from the top of viewport for better visibility
        const viewportHeight = window.innerHeight;
        const optimalPosition = storePosition - (viewportHeight * 0.05);
        
        // First try to find if we're in an iframe with a parent window
        try {
          // Check if we're in an iframe
          const isInIframe = window !== window.parent;
          
          if (isInIframe && window.frameElement) {
            // We're in an iframe, scroll the specific element into view instead
            parentStoreRow.scrollIntoView({
              behavior: 'smooth',
              block: 'start', // Align at the top
            });
            
            // For iframes with scrollable content
            if (parentStoreRow.scrollIntoViewIfNeeded) {
              parentStoreRow.scrollIntoViewIfNeeded(true);
            }
          } else {
            // Standard scrolling for normal websites
            window.scrollTo({
              top: Math.max(0, optimalPosition),
              behavior: 'smooth'
            });
          }
        } catch (e) {
          // Fallback if any cross-origin issues
          console.log('Using fallback scroll method');
          // Use the most compatible method
          window.scrollTo({
            top: Math.max(0, optimalPosition),
            behavior: 'smooth'
          });
        }
      }
    };

    // First, check if there are any other expanded sections currently open
    const handleTransition = () => {
      const allExpandedSections = document.querySelectorAll('.expanded-store-section');
      const currentSection = sectionRef.current;
      
      // If there are other expanded sections already open and we're not one of them
      if (allExpandedSections.length > 1 && currentSection) {
        // Add a 'entering' class to our section to control the animation
        currentSection.classList.add('entering');
        
        // Temporarily hide our section
        currentSection.style.opacity = '0';
        currentSection.style.maxHeight = '0';
        currentSection.style.overflow = 'hidden';
        
        // Use a very short timeout to let the browser apply these styles
        setTimeout(() => {
          // Smoothly show our section
          currentSection.style.transition = 'opacity 0.3s ease, max-height 0.4s ease';
          currentSection.style.opacity = '1';
          currentSection.style.maxHeight = '2000px'; // Large enough to accommodate any content
          currentSection.style.overflow = 'visible';
          
          // Scroll to our section after a delay
          setTimeout(() => {
            scrollToStore();
            // Remove the temporary class and inline styles
            setTimeout(() => {
              currentSection.classList.remove('entering');
              currentSection.style.transition = '';
              currentSection.style.maxHeight = '';
            }, 400);
          }, 100);
        }, 50);
      } else {
        // If no other sections are open, just scroll to our section
        setTimeout(scrollToStore, 250);
      }
    };
    
    // Execute the transition logic
    handleTransition();
  }, []); // Empty dependency array means this runs once on mount

  return (
    <div className="expanded-store-section" ref={sectionRef}>
      <div className="expanded-header">
        <div className="expanded-heading">
          <span className="expanded-eyebrow">In-store availability</span>
          <h3>Available products</h3>
          <p>{products.length} {products.length === 1 ? 'product' : 'products'} currently listed</p>
        </div>

        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="directions-button"
            onClick={handleGetDirections}
          >
            <span className="directions-icon">
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path d="M10 18s6-5.4 6-10A6 6 0 1 0 4 8c0 4.6 6 10 6 10Z" />
                <circle cx="10" cy="8" r="2" />
              </svg>
            </span>
            <span className="directions-text">Get directions</span>
          </a>
        )}
      </div>
      
      {/* Swipe-enabled product list container */}
      <div 
        ref={swipeAreaRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ 
          cursor: isMobile ? 'grab' : 'default',
          userSelect: 'none',
          position: 'relative'
        }}
      >
        <div 
          className="expanded-product-list"
          role="list"
          aria-label="Products available at this store"
          ref={productListRef}
        >
          {loading ? (
            <div className="loading-products-message">
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : currentProducts.length > 0 ? (
            currentProducts.map(product => {
              const config = product.configs[0];
              const hasRecUrl = !!product.recreationalURL && product.recreationalURL.startsWith('http');
              const hasMedUrl = !!product.medicalURL && product.medicalURL.startsWith('http');
              const hasValidUrl = hasRecUrl || hasMedUrl;
              const canonical = resolveCanonicalProduct(product, () => canonicalProductCache.get(product.name) || getCanonicalProductSync(product.name) || null);
              const onSale = config && config.salePrice && config.salePrice < config.normalPrice;
              // Use rec URL if available, else med
              const productUrl = hasRecUrl ? product.recreationalURL : product.medicalURL;
              
              return (
                <ModalProductCard
                  key={product.id}
                  product={product}
                  config={config}
                  canonicalProduct={canonical}
                  onSale={onSale}
                  hasValidUrl={hasValidUrl}
                  small={true}
                  distance={location.fields && typeof location.fields.distance === 'number' ? location.fields.distance : null}
                  city={location.fields?.City}
                  state={location.fields?.State}
                  onViewDetails={() => {
                    if (hasValidUrl && config?.currentStock > 0) {
                      window.open(productUrl, '_blank', 'noopener,noreferrer');
                      trackProductView(product, location, canonical);
                    }
                  }}
                />
              );
            })
          ) : (
            <div className="no-products-message">
              No products available at this location.
            </div>
          )}
        </div>
        
        {/* Swipe hint for mobile users - shows only if user hasn't swiped yet */}
        {isMobile && !hasUserSwiped && totalPages > 1 && (
          <div className="swipe-hint">
            <span className="swipe-hint-arrow">←</span>
            <span>Swipe for more products</span>
            <span className="swipe-hint-arrow">→</span>
          </div>
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            className="pagination-button prev"
            aria-label="Previous page"
          >
            <span className="arrow-icon">←</span> 
            <span className="button-text">Previous</span>
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            className="pagination-button next"
            aria-label="Next page"
          >
            <span className="button-text">Next</span>
            <span className="arrow-icon">→</span>
          </button>
        </div>
      )}
      
      <div className="expanded-footer">
        <div className="price-disclaimer">
          Prices shown are approximate and may not include taxes. Product availability and pricing 
          may vary at time of purchase.
        </div>
      </div>
    </div>
  );
});

ExpandedStoreSection.propTypes = {
  products: PropTypes.array.isRequired,
  location: PropTypes.object.isRequired,
  loading: PropTypes.bool
};

export default ExpandedStoreSection;
